import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Role } from 'consts/auth';
import { useHasRole, useIsPLOnly } from 'hooks/use-auth';
import { LoadingPage } from 'pages';
import React from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';

// HACK: PL-only users may only land on the Petro-Logistics Shiptracking page.
// This is a frontend-only redirect — backend APIs are not gated. Move to a
// proper backend allowlist + MCP server guard ASAP.
const PL_ONLY_ALLOWED_PATH = '/petrologistics-shiptracking';

interface PrivateRouteProps {
  role?: Role;
}

export function PrivateRoute ({
  component: Component,
  children,
  render,
  role = Role.BasicAccess,
  path,
  ...args
}: PrivateRouteProps & RouteProps) {
  const userHasRole = useHasRole(role);
  const hasFullAccess = useHasRole(Role.FullAccess);
  const isPLOnly = useIsPLOnly();
  const userCanAccess = userHasRole || hasFullAccess;
  // HACK: PL-only users get bounced off any route that isn't the PL page.
  const blockedForPLOnly = isPLOnly && path !== PL_ONLY_ALLOWED_PATH;

  if (children) {
    throw new Error('children not supported for private routes');
  }

  if (render) {
    throw new Error('render not supported for private routes');
  }

  if (!Component) {
    throw new Error('component to render must be defined');
  }

  // Memoize the protected wrapper ONCE
  const ProtectedComponent = React.useMemo(() => {
    return withAuthenticationRequired<RouteComponentProps>((auth0Props) => {
      // HACK: PL-only short-circuit — sends user to the only page they may use.
      if (blockedForPLOnly) {
        return <Redirect to={PL_ONLY_ALLOWED_PATH} />;
      }
      if (userCanAccess) {
        return <Component {...auth0Props} />;
      }
      return <Redirect to="/sign-up" />;
    }, {
      onRedirecting: () => (
        <LoadingPage message="Redirecting you to the login page..." />
      ),
      returnTo: () => window.location.pathname,
    });
  }, [Component, userCanAccess, blockedForPLOnly]);

  return <Route {...args} path={path} component={ProtectedComponent} />;
}
